import { useCallback } from 'react';
import { getPosthog, AnalyticsEvent } from '@/lib/analytics';

export interface UserIdentity {
  email?: string;
  name?: string;
  role?: string;
  organization_id?: string;
  plan?: string;
  subscription_status?: string | null;
  created_at?: string;
}

export interface OrganizationProps {
  plan?: string;
  subscription_status?: string | null;
  members_count?: number;
  os_count_total?: number;
  created_at?: string;
}

function safely(fn: () => void) {
  try {
    fn();
  } catch (err) {
    if (import.meta.env.DEV) console.warn('[analytics]', err);
  }
}

export function useAnalytics() {
  const track = useCallback((event: AnalyticsEvent, props?: Record<string, unknown>) => {
    const posthog = getPosthog();
    if (!posthog) return;
    safely(() => posthog.capture(event, props ?? {}));
  }, []);

  const identify = useCallback((userId: string, props: UserIdentity) => {
    const posthog = getPosthog();
    if (!posthog) return;
    safely(() => posthog.identify(userId, props));
  }, []);

  const group = useCallback((organizationId: string, props: OrganizationProps) => {
    const posthog = getPosthog();
    if (!posthog) return;
    safely(() => posthog.group('organization', organizationId, props));
  }, []);

  const reset = useCallback(() => {
    const posthog = getPosthog();
    if (!posthog) return;
    safely(() => posthog.reset());
  }, []);

  const getUserProperty = useCallback((key: string): unknown => {
    const posthog = getPosthog();
    if (!posthog) return undefined;
    try {
      return posthog.get_property(key);
    } catch {
      return undefined;
    }
  }, []);

  const setUserProperties = useCallback((props: Record<string, unknown>) => {
    const posthog = getPosthog();
    if (!posthog) return;
    safely(() => posthog.people.set(props));
  }, []);

  return { track, identify, group, reset, getUserProperty, setUserProperties };
}
